import {
  BuildingType, BUILDING_COST, BUILDING_SIZE, BUILDING_STAGES,
  ENERGY_COST, MILL_INPUT_WHEAT, MILL_INPUT_CORN,
  SimState, totalBuildingCost, XP_REWARDS,
} from './types';
import { EnergySystem } from './EnergySystem';
import { ProgressionSystem } from './ProgressionSystem';
import { PlotSystem } from './PlotSystem';
import { EventBus } from './events';

/**
 * Building purchase + placement. (Selling crops goes through the TruckSystem.)
 */
export const EconomySystem = {
  /** Can the player afford the FIRST stage to start construction. */
  canAfford(state: SimState, type: BuildingType): boolean {
    const stages = BUILDING_STAGES[type];
    if (stages.length === 0) return true;
    const s = stages[0];
    return (
      state.res.money  >= (s.money ?? 0) &&
      state.res.wood   >= (s.wood ?? 0) &&
      state.res.basket >= (s.wheat ?? 0) &&
      (state.res.stone ?? 0) >= (s.stone ?? 0)
    );
  },

  /** Total commitment to fully build — used by Build panel for clarity. */
  totalCost(type: BuildingType) {
    return totalBuildingCost(type);
  },

  canPlace(state: SimState, c: number, r: number, type: BuildingType): boolean {
    if (!this.canAfford(state, type)) return false;
    return PlotSystem.canBuildAt(state, c, r, type);
  },

  /** Pay for stage 1 and place a building at (c, r) in "under construction" state. */
  placeBuilding(state: SimState, c: number, r: number, type: BuildingType, bus: EventBus): boolean {
    const stages = BUILDING_STAGES[type];
    const s1 = stages[0] ?? { money: 0, wood: 0, wheat: 0, stone: 0 };
    const need = (k: 'money' | 'wood' | 'wheat' | 'stone') => s1[k] ?? 0;
    if (state.res.money  < need('money')) { bus.emit({ type: 'blocked', reason: `Need ${need('money')} coins` }); return false; }
    if (state.res.wood   < need('wood'))  { bus.emit({ type: 'blocked', reason: `Need ${need('wood')} wood — chop some trees!` }); return false; }
    if (state.res.basket < need('wheat')) { bus.emit({ type: 'blocked', reason: `Need ${need('wheat')} wheat` }); return false; }
    if ((state.res.stone ?? 0) < need('stone')) { bus.emit({ type: 'blocked', reason: `Need ${need('stone')} stone — break boulders with the pickaxe.` }); return false; }
    if (!PlotSystem.canBuildAt(state, c, r, type)) {
      bus.emit({ type: 'blocked', reason: 'Cannot build here — clear or till the tile first.' });
      return false;
    }
    // Reject any tile owned by an adopted plot — building on farmland
    // erases the player's work and confuses the plot UI.
    const { w, d } = BUILDING_SIZE[type];
    for (const p of state.plots ?? []) {
      for (let dr = 0; dr < d; dr++) for (let dc = 0; dc < w; dc++) {
        if (p.tiles.some(t => t.c === c + dc && t.r === r + dr)) {
          bus.emit({ type: 'blocked', reason: 'Cannot build inside an adopted plot.' });
          return false;
        }
      }
    }
    if (!EnergySystem.spend(state, ENERGY_COST.buyBuilding)) {
      bus.emit({ type: 'blocked', reason: 'Not enough energy' });
      return false;
    }
    state.res.money  -= need('money');
    state.res.wood   -= need('wood');
    state.res.basket -= need('wheat');
    if (need('stone') > 0) state.res.stone = (state.res.stone ?? 0) - need('stone');
    // Fly the initial stage's materials FROM HUD chips TO the new
    // building footprint so the placement animation matches the rest
    // of the construction flow (deposit-by-deposit visual feedback).
    if (need('money') > 0) bus.emit({ type: 'materialsDeposited', col: c, row: r, kind: 'money', qty: need('money') });
    if (need('wood')  > 0) bus.emit({ type: 'materialsDeposited', col: c, row: r, kind: 'wood',  qty: need('wood')  });
    if (need('wheat') > 0) bus.emit({ type: 'materialsDeposited', col: c, row: r, kind: 'wheat', qty: need('wheat') });
    if (need('stone') > 0) bus.emit({ type: 'materialsDeposited', col: c, row: r, kind: 'stone', qty: need('stone') });
    // Stamp every tile in the footprint with the building type + anchor pointer
    for (let dr = 0; dr < d; dr++) {
      for (let dc = 0; dc < w; dc++) {
        const t = state.plot.tiles[r + dr][c + dc];
        t.bld = type;
        t.bldAnchor = { c, r };
      }
    }
    // Prune any world-wide weeds whose tile is now covered by the new
    // building's footprint — otherwise stale weed sprites linger inside
    // walls forever and the weed array grows monotonically.
    if (state.weeds?.length) {
      const inFootprint = (wc: number, wr: number) =>
        wc >= c && wc < c + w && wr >= r && wr < r + d;
      state.weeds = state.weeds.filter(wd => !inFootprint(wd.c, wd.r));
    }
    // Construction state lives on the anchor only
    const anchorTile = state.plot.tiles[r][c];
    if (stages.length > 1) {
      anchorTile.construction = { stage: 1, totalStages: stages.length };
    }
    ProgressionSystem.addXp(state, XP_REWARDS.buyBuilding, bus);
    bus.emit({ type: 'buildingPlaced', col: c, row: r, bld: type });
    return true;
  },

  /** Tear down a building at the given coords. Refund is 50% of what the
   *  player has ACTUALLY PAID so far — for a half-built mill that's only
   *  half of the completed stages, not the full cost. Fixtures (mailbox,
   *  depot) cannot be demolished. */
  demolishBuilding(state: SimState, c: number, r: number, bus: EventBus): boolean {
    const t = state.plot.tiles[r]?.[c];
    if (!t || !t.bld) return false;
    if (t.bld === 'mailbox' || t.bld === 'truck-depot') {
      bus.emit({ type: 'blocked', reason: 'Fixtures can\'t be removed.' });
      return false;
    }
    const anchor = t.bldAnchor ?? { c, r };
    const type = t.bld;
    // Compute what the player has paid so far. If the building is still
    // under construction, `construction.stage` is the number of stages
    // ALREADY COMPLETED; refund stages [0, stage). Otherwise refund the
    // full total cost (fully built).
    const anchorTile = state.plot.tiles[anchor.r]?.[anchor.c];
    const stages = BUILDING_STAGES[type];
    const completed = anchorTile?.construction?.stage ?? stages.length;
    const paid = { money: 0, wood: 0, wheat: 0, stone: 0 };
    for (let i = 0; i < completed; i++) {
      const s = stages[i];
      if (!s) continue;
      paid.money += s.money ?? 0;
      paid.wood  += s.wood  ?? 0;
      paid.wheat += s.wheat ?? 0;
      paid.stone += s.stone ?? 0;
    }
    // If the building model has no staged costs (e.g. legacy 1-stage), fall
    // back to BUILDING_COST so we don't refund nothing on old saves.
    if (paid.money === 0 && paid.wood === 0 && paid.wheat === 0 && paid.stone === 0) {
      const legacy = BUILDING_COST[type];
      paid.money = legacy.money;
      paid.wood  = legacy.wood;
    }
    const refund = {
      money: Math.floor(paid.money * 0.5),
      wood:  Math.floor(paid.wood  * 0.5),
      wheat: Math.floor(paid.wheat * 0.5),
      stone: Math.floor(paid.stone * 0.5),
    };
    const { w, d } = BUILDING_SIZE[type];
    // Refund any wheat/corn locked in the mill queue too — the player paid
    // for those batches, and the head batch's first unit input is already
    // spent into millInputUsed. Both the in-flight unit and any pending
    // queue units are returned to storage so cancelling a running mill
    // doesn't vaporize grain.
    if (type === 'mill' && anchorTile) {
      const queue = anchorTile.millQueue ?? [];
      const grinding = (anchorTile.millRemaining ?? 0) > 0;
      for (let i = 0; i < queue.length; i++) {
        const entry = queue[i];
        const isHead = i === 0;
        const inputCost = entry.kind === 'wheat' ? MILL_INPUT_WHEAT : MILL_INPUT_CORN;
        // In-flight unit at the queue head: refund the stored `millInputUsed`
        // so legacy values that may differ from the current per-batch cost
        // still round-trip cleanly.
        if (isHead && grinding) {
          const used = anchorTile.millInputUsed ?? inputCost;
          if (entry.kind === 'wheat') state.res.basket += used;
          else                        state.res.corn   += used;
        }
        // Pending un-started units in this entry — restore their input cost.
        const pendingUnits = Math.max(0, entry.qty - (isHead && grinding ? 1 : 0));
        if (entry.kind === 'wheat') state.res.basket += pendingUnits * inputCost;
        else                        state.res.corn   += pendingUnits * inputCost;
      }
    }
    // Clear every footprint tile.
    for (let dr = 0; dr < d; dr++) {
      for (let dc = 0; dc < w; dc++) {
        const ft = state.plot.tiles[anchor.r + dr]?.[anchor.c + dc];
        if (!ft) continue;
        ft.bld = null;
        delete ft.bldAnchor;
        delete ft.construction;
        delete ft.millRemaining;
        delete ft.millInputUsed;
        delete ft.millQueue;
      }
    }
    state.res.money += refund.money;
    state.res.wood  += refund.wood;
    if (refund.wheat > 0) state.res.basket += refund.wheat;
    if (refund.stone > 0) state.res.stone = (state.res.stone ?? 0) + refund.stone;
    bus.emit({ type: 'buildingPlaced', col: anchor.c, row: anchor.r, bld: type });   // re-uses event to trigger renderer rebuild
    const parts: string[] = [];
    if (refund.money > 0) parts.push(`${refund.money}🪙`);
    if (refund.wood  > 0) parts.push(`${refund.wood}🪵`);
    if (refund.wheat > 0) parts.push(`${refund.wheat}🌾`);
    if (refund.stone > 0) parts.push(`${refund.stone}🪨`);
    const refundStr = parts.length > 0 ? parts.join(' + ') : 'nothing';
    bus.emit({ type: 'hint', text: `Demolished ${type} — refunded ${refundStr}.` });
    return true;
  },
};
